import type { FlowMediaPlaylistsDto } from '@/types/dtos/media/media-playlist.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import {
  isEmbeddedDraftLayerSession,
  pickNewerSession,
  resolveSessionOriginIdFromFlowSlot,
} from '@/components/flows/detail/flow-detail.utils';
import { getObjectId } from '@/helpers/object.id.helper';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { getAppQueryClient } from '@/state/query-client-holder';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';

function slotHasEmbeddedSessionPlaylists(
  content: FlowSlotDto['content'],
): content is Extract<FlowSlotDto['content'], { mediaPlaylists?: unknown }> {
  return typeof content === 'object' && content !== null && 'mediaPlaylists' in content;
}

/** Keep session playlists from whichever flow document actually embeds slot.content. */
export function mergeFlowDocumentsForSimulation(primary?: FlowDto, secondary?: FlowDto): FlowDto | undefined {
  if (!primary) {
    return secondary;
  }
  if (!secondary || primary.id !== secondary.id) {
    return primary;
  }

  const primarySlots = primary.slots;
  const secondarySlots = secondary.slots;
  if (!primarySlots?.length || !secondarySlots?.length) {
    return primary;
  }

  let slotsChanged = false;
  const slots = primarySlots.map((slot) => {
    if (slotHasEmbeddedSessionPlaylists(slot.content)) {
      return slot;
    }
    const otherSlot = secondarySlots.find((entry) => entry.id === slot.id);
    if (otherSlot && slotHasEmbeddedSessionPlaylists(otherSlot.content)) {
      slotsChanged = true;
      return { ...slot, content: otherSlot.content };
    }
    return slot;
  });

  return slotsChanged ? { ...primary, slots } : primary;
}

function findEventSessionByOriginId(sessionOriginId: string, sessions?: SessionDto[]): SessionDto | undefined {
  return sessions?.find((session) => {
    const originId = session.origin ? getObjectId(session.origin) : session.id;
    return originId === sessionOriginId || session.id === sessionOriginId;
  });
}

/** Same session resolution as flow-control-slot-media-below-row (store, query cache, embedded PLAN). */
export function resolveSessionForFlowSlot(flow: FlowDto, flowSlotId: string): SessionDto | undefined {
  const slot = flow.slots?.find((entry) => entry.id === flowSlotId);
  if (!slot || slot.contentType !== 'Session') {
    return undefined;
  }

  const { eventSessionsPlan, eventSessionsOrganize } = useEventStore.getState();
  const sessionOriginId = resolveSessionOriginIdFromFlowSlot(slot, {
    eventSessionsPlan,
    eventSessionsOrganize,
    flowPlan: flow.mode === 'PLAN' ? flow : undefined,
  });

  const planSessionFromStore = sessionOriginId
    ? findEventSessionByOriginId(sessionOriginId, eventSessionsPlan)
    : undefined;

  const queryClient = getAppQueryClient();
  const sessionFromQuery =
    sessionOriginId && queryClient
      ? queryClient.getQueryData<SessionDto>([GET_SESSION_QUERY_KEY, sessionOriginId, 'PLAN'])
      : undefined;

  const embedded = embeddedSessionContent(slot.content);
  const slotSession =
    embedded && (embedded.mode === 'PLAN' || isEmbeddedDraftLayerSession(embedded.mode)) ? embedded : undefined;

  return pickNewerSession(planSessionFromStore, sessionFromQuery) ?? slotSession;
}

/** @deprecated Use resolveSessionForFlowSlot */
export function resolveEventPlanSessionForFlowSlot(flow: FlowDto, flowSlotId: string): SessionDto | undefined {
  return resolveSessionForFlowSlot(flow, flowSlotId);
}

function embeddedSessionContent(content: FlowSlotDto['content']): SessionDto | undefined {
  if (typeof content !== 'object' || content === null) {
    return undefined;
  }
  return content as SessionDto;
}

/** Match flow-control session media list: event plan session is authoritative for playlists. */
function mergeSessionSlotContent(slot: FlowSlotDto, planSession?: SessionDto): FlowSlotDto['content'] {
  const embedded = embeddedSessionContent(slot.content);
  if (!planSession) {
    return embedded ?? slot.content;
  }
  return {
    ...(embedded ?? planSession),
    ...planSession,
    mediaPlaylists: planSession.mediaPlaylists ?? embedded?.mediaPlaylists,
    media:
      (planSession.media?.length ?? 0) >= (embedded?.media?.length ?? 0)
        ? planSession.media
        : (embedded?.media ?? planSession.media),
  };
}

function sessionSlotContentFingerprint(content: FlowSlotDto['content']): string {
  const session = embeddedSessionContent(content);
  if (!session) {
    return typeof content === 'string' ? content : '';
  }
  return JSON.stringify({
    mediaPlaylists: session.mediaPlaylists ?? null,
    mediaIds: session.media?.map((item) => item.id) ?? [],
  });
}

/**
 * Session playlists in control tab come from eventSessionsPlan, not flow slot.content.
 * Inject them so simulation show_info / playability matches the media list.
 */
export function mergeEventSessionsIntoFlow(flow: FlowDto): FlowDto {
  if (!flow.slots?.length) {
    return flow;
  }

  let slotsChanged = false;
  const slots = flow.slots.map((slot) => {
    if (slot.contentType !== 'Session') {
      return slot;
    }

    const resolvedSession = resolveSessionForFlowSlot(flow, slot.id);
    const mergedContent = mergeSessionSlotContent(slot, resolvedSession);
    if (sessionSlotContentFingerprint(mergedContent) === sessionSlotContentFingerprint(slot.content)) {
      return slot;
    }

    slotsChanged = true;
    return { ...slot, content: mergedContent };
  });

  return slotsChanged ? { ...flow, slots } : flow;
}

/**
 * Plan flow for simulation cue enrichment — merges flowPlan, flow, and the control-tab
 * `flow` prop so session playlists on embedded slot.content are not lost when only flowPlan
 * was updated over the websocket.
 */
export function resolveFlowControlSimulationFlow(fallback?: FlowDto): FlowDto | undefined {
  const { flowPlan, flow } = useFlowStore.getState();
  const candidates = [flowPlan, flow, fallback].filter((entry): entry is FlowDto => entry != null);
  if (candidates.length === 0) {
    return undefined;
  }

  let merged = candidates[0]!;
  for (let index = 1; index < candidates.length; index += 1) {
    merged = mergeFlowDocumentsForSimulation(merged, candidates[index]!) ?? merged;
  }
  return mergeEventSessionsIntoFlow(merged);
}

function mergeFlowMediaPlaylists(primary?: FlowDto, secondary?: FlowDto): FlowMediaPlaylistsDto | undefined {
  const primaryPlaylists = primary?.mediaPlaylists;
  const secondaryPlaylists = secondary?.mediaPlaylists;
  if (!primaryPlaylists && !secondaryPlaylists) {
    return undefined;
  }

  return {
    ...secondaryPlaylists,
    ...primaryPlaylists,
    slots: {
      ...secondaryPlaylists?.slots,
      ...primaryPlaylists?.slots,
    },
  };
}

/** Plan flow merged with live flow — session slot content, event sessions, and break slot playlists. */
export function resolveFlowControlPlaylistFlow(fallback?: FlowDto): FlowDto | undefined {
  const { flowPlan, flow } = useFlowStore.getState();
  const base = resolveFlowControlSimulationFlow(fallback);
  if (!base) {
    return undefined;
  }

  const playlists = mergeFlowMediaPlaylists(flowPlan ?? undefined, flow ?? undefined);
  if (!playlists) {
    return base;
  }

  return {
    ...base,
    mediaPlaylists: {
      ...base.mediaPlaylists,
      ...playlists,
    },
  };
}

/** Fingerprint of session playlists resolved from store + query cache (for hydrate refresh). */
export function sessionPlaylistFingerprintForFlow(flow?: FlowDto): string {
  if (!flow?.slots?.length) {
    return '';
  }
  return flow.slots
    .filter((slot) => slot.contentType === 'Session')
    .map((slot) => {
      const session = resolveSessionForFlowSlot(flow, slot.id);
      return `${slot.id}:${JSON.stringify(session?.mediaPlaylists?.session ?? null)}`;
    })
    .join('|');
}

/** Stable key for playability-only hydrate — avoids resetting simulation on unrelated flow churn. */
export function simulationFlowPlayabilityKey(flow?: FlowDto): string {
  if (!flow) {
    return '';
  }
  const parts = [flow.version ?? flow.id ?? '', JSON.stringify(flow.mediaPlaylists ?? null)];
  for (const slot of flow.slots ?? []) {
    if (slotHasEmbeddedSessionPlaylists(slot.content)) {
      parts.push(`${slot.id}:${JSON.stringify(slot.content.mediaPlaylists ?? null)}`);
    }
  }
  return parts.join('|');
}
